import { WBBox, WBFlex } from '@admiin-com/ds-web';
import { Step, StepContent, StepLabel, Stepper, styled } from '@mui/material';
import React from 'react';
import { UploadBills } from './Upload';
import { ReviewBills } from './Review';
import { useTranslation } from 'react-i18next';
import XModal from '../XModal';
import InvoiceRenderer from '../InvoiceRenderer';
import { UploadBillsEdit } from './Upload/UploadBillsEdit';
import {
  Contact,
  ContactBpay,
  DocumentAnalysis,
  DocumentAnalysisBankAccount,
  DocumentAnalysisContact,
  DocumentAnalysisLineItemInput,
  Entity,
  PaymentFrequency,
  Task,
  DocumentType,
  TaskSettlementStatus,
} from '@admiin-com/ds-graphql';
import { DATE_FORMATS, S3Upload } from '@admiin-com/ds-common';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { useConvertTaskToBill } from './useConvertTaskToBill';
import { useDefaultValueForFromTo } from '../../features/providers/TaskCreationProvider/TaskCreationProvider';
import { useScanning } from './Review/useScanning';
import { useDefaultFrom } from './hooks/useDefaultFrom';
import { useSetPreviousScans } from './hooks/useSetPreviousScans';
import { smartMergeDocumentAnalysis } from '../../helpers/documentAnalysisMerge';
import { transformDocumentAnalysisToBill } from './Upload/transformer';
import { isBillLoading } from '../../helpers/tasks';

export interface Bill {
  from?: Contact | Entity | DocumentAnalysisContact | null;
  to?: Entity | null;
  amount?: number;
  dueAt: string;
  issuedAt?: string;
  lineItems: DocumentAnalysisLineItemInput[];
  reference?: string;
  document: S3Upload | null;
  checked: boolean;
  documentAnalysis?: DocumentAnalysis;
  bpay?: ContactBpay | null;
  saveBpayRefForFuture?: boolean;
  bank?: DocumentAnalysisBankAccount | null;
  documentSize?: string;
  documentName?: string;
  errors?: { [key: string]: string };
  paymentFrequency?: PaymentFrequency;
  taxDocumentType?: DocumentType;
  taxSettlementStatus?: TaskSettlementStatus;
  period?: string;
  year?: string;
}

interface Props {
  draftTask: Task | null;
  onClose: () => void;
}

export const PayBillCreateForm = (props: Props) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const { t } = useTranslation();

  const handleNext = React.useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }, []);

  const methods = useForm<{ bills: Bill[] }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { bills: [] },
  });
  const bills = useWatch({ name: 'bills', control: methods.control });

  const { fields, append, remove, replace, update } = useFieldArray({
    control: methods.control,
    name: 'bills',
  });

  const appendBill = (bill: Bill) => {
    for (const field of fields) {
      if (
        field.documentAnalysis?.documents?.[0]?.key ===
          bill.documentAnalysis?.documents?.[0]?.key ||
        field.document?.key === bill.document?.key
      ) {
        return;
      }
    }
    append(bill);
  };

  const removeBill = (key: string) => {
    remove(fields.findIndex((field) => field.document?.key === key));
  };

  const setBills = (bills: Bill[]) => {
    replace(bills);
  };

  const updateBillFromDocumentAnalysis = (
    documentAnalysis: DocumentAnalysis
  ) => {
    const index = fields.findIndex(
      (field) => field.document?.key === documentAnalysis.documents?.[0]?.key
    );
    const bill = fields[index];
    if (bill) {
      const mergedAnalysis = smartMergeDocumentAnalysis(
        bill.documentAnalysis,
        documentAnalysis
      );

      const transformedBillData = transformDocumentAnalysisToBill(
        mergedAnalysis,
        defaultFrom
      );
      update(index, {
        ...bill,
        documentAnalysis: mergedAnalysis,
        ...transformedBillData,
        to: entity,
      });
    }
  };

  const entity = useDefaultValueForFromTo();
  const [preivewModalOpen, setPreviewModalOpen] = React.useState(false);
  const defaultFrom = useDefaultFrom();
  const onEnterManually = () => {
    handleNext();
    setBills([
      {
        to: entity,
        amount: 0,
        dueAt: DateTime.now().toFormat(DATE_FORMATS.BACKEND_DATE),
        issuedAt: DateTime.now().toFormat(DATE_FORMATS.BACKEND_DATE),
        lineItems: [],
        from: defaultFrom,
        reference: '',
        paymentFrequency: PaymentFrequency.ONCE,
        checked: true,
        document: null,
      },
    ]);
    setIsManualInput(true);
  };

  const convertTaskToBill = useConvertTaskToBill();
  React.useEffect(() => {
    if (props.draftTask) {
      convertTaskToBill(props.draftTask).then((bill) => {
        handleNext();
        setIsManualInput(true);
        setBills([bill]);
      });
    }
  }, [props.draftTask]);

  const [searchParams, setSearchParams] = useSearchParams();

  const handleChangeStepParams = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('step', value);
    setSearchParams(params);
  };
  React.useEffect(() => {
    handleChangeStepParams(activeStep.toString());
  }, [activeStep]);

  const onNext = () => {
    // runBillsAnalysis(entityId, newBills);
    handleNext();
    setIsManualInput(false);
  };

  const [scanning, setScanning] = React.useState(false);

  useScanning({ updateBill: updateBillFromDocumentAnalysis, setScanning });

  React.useEffect(() => {
    if (bills.every((bill) => !isBillLoading(bill.documentAnalysis))) {
      setScanning(false);
    }
  }, [bills, setScanning]);

  const { hasMoreData, loadMore, loading } = useSetPreviousScans({
    appendBill,
  });

  const [isManualInput, setIsManualInput] = React.useState(false);

  const steps = [
    {
      label: t('uploadBills', { ns: 'taskbox' }),
      description: (
        <UploadBills
          onEnter={onEnterManually}
          onNext={onNext}
          setBills={setBills}
          bills={bills}
          hasMoreData={hasMoreData}
          loadingScanned={loading}
          loadMoreScanned={loadMore}
          appendBill={appendBill}
          removeBill={removeBill}
        />
      ),
    },
    {
      label: t('reviewBills', { ns: 'taskbox' }),
      description: (
        <ReviewBills
          draftTaskId={props.draftTask?.id ?? null}
          onClose={props.onClose}
          scanning={scanning}
          setScanning={setScanning}
          bills={bills}
          setBills={setBills}
          manualBillInput={isManualInput}
        />
      ),
    },
  ];

  return (
    <WBBox width="100%">
      <FormProvider {...methods}>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          connector={<></>}
        >
          {steps.map((step, index) => (
            <Step key={step.label} sx={{ mt: 0 }}>
              <StyledLabel
                onClick={() => {
                  if (index < activeStep) setActiveStep(index);
                }}
              >
                <WBFlex alignItems={'center'} justifyContent={'space-between'}>
                  {step.label}
                  {activeStep !== 0 && index === 0 && (
                    <UploadBillsEdit billsCount={bills.length} />
                  )}
                </WBFlex>
              </StyledLabel>
              <StepContent TransitionProps={{ style: { width: '100%' } }}>
                {step.description}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </FormProvider>
      <XModal
        open={preivewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
        }}
      >
        <InvoiceRenderer />
      </XModal>
    </WBBox>
  );
};
const StyledLabel = styled(StepLabel)(({ theme }) => ({
  cursor: 'pointer',
}));
